import type { ReactElement } from 'react';
import { useCallback, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { DataTable } from '@/components/DataTable';
import type { DataFetchParams } from '@/components/DataTable';
import type {
  BulkToolbarContext,
  RowSelectionOptions,
} from '@/components/DataTable/types';
import type {
  AlertVisibility,
  GroupedAlert,
} from '@kirin/api-client/contracts/alerts';
import { queryKeys } from '@/hooks/api/queryKeys';
import { AlertVisibilitySelector } from '@/components/AlertVisibilitySelector';
import { useAlertVisibility } from '@/hooks/useAlertVisibility';
import { useAlertsIdeColumnEnabled } from '@/hooks/useAlertsIdeColumnEnabled';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { flagsmithFeatureFlags } from '@/lib/flagsmith';
import { sanitizeAlertsParams } from '@/components/DataTable/alerts/sanitizeAlertsParams';
import {
  useCanViewAdminContent,
  useIsOwnerOrManager,
  useOrgWriteGate,
} from '@/hooks/useRoleCheck';
import { useTimestampFormat } from '@/hooks/useTimestampFormat';
import {
  createAlertsColumns,
  createOrganizationAlertsActionsColumn,
} from './columns';
import { AlertsBulkActionBar } from '@/components/AlertsBulkActions/AlertsBulkActionBar';
import { fetchOrganizationAlerts } from './api';
import { AlertDetailCard } from '@/components/grouped-alerts/AlertDetailCard';
import { Button } from '@/components/ui/button';
import { ChevronRight, List } from 'lucide-react';
import { ALERTS_TABLE_CONFIG } from './utils';

/**
 * Generate query keys for the DataTable.
 *
 * `visibility` is appended to the key only when it is not `'active'` so
 * toggling away from the default triggers a refetch instead of returning
 * the previously-cached default-list page. The default value is omitted
 * entirely so the cache key for the default-list baseline stays
 * byte-identical to the pre-KIR-2512 wire shape.
 */
function getQueryKeysFromParams(
  organizationId: string,
  params: DataFetchParams,
  visibility: AlertVisibility
) {
  const search = params.search;
  const baseKey = queryKeys.organizations.groupedAlerts(organizationId, {
    page: params.pagination.pageIndex + 1,
    limit: params.pagination.pageSize,
    filters: params.filters,
    sortBy: params.sort?.field,
    sortOrder: params.sort?.order,
    search,
  });
  if (visibility === 'active') return baseKey;
  return [...baseKey, { visibility }];
}

function buildOrgScopeKey(organizationId: string): string {
  return `org:${organizationId}`;
}

/**
 * DataTable-based alerts tab for organization
 */
export function DataTableAlertsContent({
  organizationId,
}: {
  organizationId: string;
}): ReactElement {
  const ideColumnEnabled = useAlertsIdeColumnEnabled();
  const { flags } = useFeatureFlags([
    flagsmithFeatureFlags.ALERTS_REVAMP,
    flagsmithFeatureFlags.EDIT_POLICY_FROM_ALERTS,
    flagsmithFeatureFlags.ALERT_MUTING,
    flagsmithFeatureFlags.ALERT_IGNORE,
    flagsmithFeatureFlags.ENDPOINT_IDENTIFIER,
    flagsmithFeatureFlags.SECURITY_GROUPS_POLICIES,
  ]);
  const revampEnabled = flags[flagsmithFeatureFlags.ALERTS_REVAMP] === true;
  const endpointsColumnEnabled =
    flags[flagsmithFeatureFlags.ENDPOINT_IDENTIFIER] ?? false;
  // KIR-3800. Org alerts only — this table is the organization view, and the
  // group column is meaningless in the personal dashboard (one user, one scope).
  const securityGroupColumnEnabled =
    flags[flagsmithFeatureFlags.SECURITY_GROUPS_POLICIES] ?? false;
  const editPolicyEnabled =
    flags[flagsmithFeatureFlags.EDIT_POLICY_FROM_ALERTS] ?? false;
  const mutingEnabled = flags[flagsmithFeatureFlags.ALERT_MUTING] === true;
  const ignoreEnabled = flags[flagsmithFeatureFlags.ALERT_IGNORE] === true;

  // Read-gate: owners, managers, and support can all view org-wide content
  // (including the user-filterable column backed by the members list).
  const { canViewAdminContent, isLoading: canViewLoading } =
    useCanViewAdminContent(organizationId);

  // Write-gate for bulk actions. Owners/managers get working actions; support
  // gets the selection UI with the action buttons DISABLED ("disable, don't
  // hide", matching the per-row buttons); members get nothing. Distinct from
  // the read-gate above.
  const writeGate = useOrgWriteGate(organizationId);

  const scopeKey = buildOrgScopeKey(organizationId);
  const { visibility, setVisibility, effectiveVisibility, anyEnabled } =
    useAlertVisibility(scopeKey);

  // Show the Actions column when a suppression flag (mute/ignore) or Edit
  // Policy is on AND the viewer can see org-wide content (owner / manager /
  // support). Support sees the column with individual write buttons rendered
  // DISABLED via useOrgWriteGate — "disable, don't hide". Members
  // (canViewAdminContent false) never see the column. The "Add to
  // Allow/Block List" action (KIR-3558) lives in the expanded row's "DETECTED
  // ON CLIENT" panel, not this column, so it doesn't gate it.
  const showActionsColumn =
    (anyEnabled || editPolicyEnabled) && !canViewLoading && canViewAdminContent;

  // The Users filter sources its options from the org members list, which is
  // RBAC-gated to owner/manager/support. Only offer the filter to viewers who
  // can actually load it; members get a non-filterable Users column and never
  // fire the members fetch (→ 403). KIR-3266.
  const canFilterByUser = !canViewLoading && canViewAdminContent;

  /*
    KIR-3800 security-group posture, stated once so the two surfaces agree:

      - Group ATTRIBUTION on an alert is alert context. Anyone who may read the
        alert may see which scope produced it — they already see the user, the
        policy and the outcome, so hiding the scope's name protects nothing.
        Hence the column itself is ungated.
      - The group ROSTER is management data. Enumerating an org's groups (the
        filter dropdown lists them all, deleted ones included) is the same
        information the Security Groups page holds, and that page is
        deliberately manager/owner — NOT managerOwnerOrSupport (see
        `constants.ts`). So the filter follows the page, not the alert.

    That is why this is owner/manager rather than `canFilterByUser`: support may
    read attribution but may not enumerate the roster. Members are excluded by
    the same gate, which also keeps them off the admin-only options endpoint.
  */
  const { isOwnerOrManager, isLoading: isOwnerOrManagerLoading } =
    useIsOwnerOrManager(organizationId);
  const canFilterBySecurityGroup = !isOwnerOrManagerLoading && isOwnerOrManager;

  // Per-user Timestamp Format → format-aware Last/First Seen column widths;
  // recreate the columns when the format toggles.
  const { value: timestampFormat } = useTimestampFormat();

  const columns = useMemo(() => {
    const base = createAlertsColumns(
      organizationId,
      ideColumnEnabled,
      revampEnabled,
      canFilterByUser,
      timestampFormat,
      endpointsColumnEnabled,
      securityGroupColumnEnabled,
      canFilterBySecurityGroup
    );
    if (!showActionsColumn) return base;
    return [...base, createOrganizationAlertsActionsColumn(organizationId)];
  }, [
    organizationId,
    showActionsColumn,
    ideColumnEnabled,
    revampEnabled,
    canFilterByUser,
    timestampFormat,
    endpointsColumnEnabled,
    securityGroupColumnEnabled,
    canFilterBySecurityGroup,
  ]);

  // Bulk selection is gated ONLY by the suppression flags (mute / ignore) +
  // the write gate — NOT by `showActionsColumn`, which also turns on for
  // `editPolicyEnabled` alone. With both suppression flags off there are no
  // working bulk actions or eligibility API, so the checkbox column + bulk bar
  // must stay hidden even when the Actions column is shown for Edit Policy.
  //
  // `!writeGate.isLoading` is load-bearing: while the role resolves the gate
  // reports `hidden: false, disabled: false`, so dropping it would flash
  // working checkboxes and a live bulk bar at members before the role lands.
  const bulkSelectionEnabled =
    (mutingEnabled || ignoreEnabled) &&
    !writeGate.isLoading &&
    !writeGate.hidden;
  const rowSelectionOptions = useMemo<
    RowSelectionOptions<GroupedAlert> | undefined
  >(
    () =>
      bulkSelectionEnabled
        ? {
            getRowId: (row: GroupedAlert) => row.alertKey,
            // Tabs (Active/All/Suppressed) swap the list without touching
            // filters/search, so feed visibility in as the reset token to clear
            // the selection on tab change.
            resetToken: effectiveVisibility,
            renderBulkToolbar: (ctx: BulkToolbarContext<GroupedAlert>) => (
              <AlertsBulkActionBar
                ctx={ctx}
                scope={{ kind: 'organization', organizationId }}
                mutingEnabled={mutingEnabled}
                ignoreEnabled={ignoreEnabled}
                visibility={effectiveVisibility}
                disabled={writeGate.disabled}
              />
            ),
          }
        : undefined,
    [
      bulkSelectionEnabled,
      organizationId,
      mutingEnabled,
      ignoreEnabled,
      effectiveVisibility,
      writeGate.disabled,
    ]
  );

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const alertsBasePath = `/${organizationId}`;
  const searchInputProps = {
    'aria-label': 'Search alerts',
  };
  const searchConfig = {
    placeholder: 'Search alerts',
    className: 'w-[260px]',
    inputProps: searchInputProps,
  };

  const sanitizeParams = useCallback(
    (params: DataFetchParams): DataFetchParams =>
      sanitizeAlertsParams(
        params,
        ideColumnEnabled,
        canFilterByUser,
        endpointsColumnEnabled
      ),
    [ideColumnEnabled, canFilterByUser, endpointsColumnEnabled]
  );

  const queryFn = useCallback(
    (params: DataFetchParams, signal?: AbortSignal) =>
      fetchOrganizationAlerts(
        organizationId,
        sanitizeParams(params),
        signal,
        effectiveVisibility === 'active' ? undefined : effectiveVisibility
      ),
    [organizationId, effectiveVisibility, sanitizeParams]
  );

  const queryKeyFn = useCallback(
    (params: DataFetchParams) =>
      getQueryKeysFromParams(
        organizationId,
        sanitizeParams(params),
        effectiveVisibility
      ),
    [organizationId, effectiveVisibility, sanitizeParams]
  );

  // Visibility affects total count — include it in the knownTotal fingerprint
  // so toggling visibility invalidates the cached total.
  const countScope = useMemo(
    () => ({ visibility: effectiveVisibility }),
    [effectiveVisibility]
  );

  // Dim a row when ANY suppression state is set. The two states are
  // orthogonal at the data layer (a row can be both muted and excluded), so
  // OR is the right combinator from the user's perspective: "this row is
  // currently suppressed somehow".
  //
  // The dim is scoped to non-Actions cells via the `[&>td:not(:last-child)]`
  // arbitrary variant rather than applied at row-level. Reason: CSS opacity
  // creates a stacking context and cascades to ALL descendants regardless of
  // any child opacity override, so a row-level `opacity-60` would also dim
  // the Mute/Unmute/Create-Exclusion/Remove-Exclusion buttons in the trailing
  // Actions cell — which users read as "the action is unavailable" (it isn't;
  // it's the primary affordance for un-suppressing the row). The Actions
  // column is always appended last by both column factories
  // (`createOrganizationAlertsActionsColumn` here,
  // `createUserAlertsActionsColumn` on the account tab) and `TableBody` only
  // ever PREPENDS the chevron expansion cell, so `:last-child` reliably
  // targets it.
  const getRowClassName = useCallback(
    (row: GroupedAlert): string | undefined =>
      row.mute?.muted || row.exclude?.excluded
        ? '[&>td:not(:last-child)]:opacity-60'
        : undefined,
    []
  );

  // KIR-2748: keep the visibility bar + table inside ONE viewport-constrained,
  // overflow-hidden flex column so the bar consumes part of the fixed height
  // budget instead of stacking on top of the table's height and pushing the
  // page column past 100vh (the outer-scroll bug). The DataTable flexes to fill
  // the remaining space (`flex-1 min-h-0`) and keeps its own inner overflow
  // scroll, so any table overflow stays inside the table, never the page.
  return (
    <div className="flex flex-col h-[calc(100vh-11rem)] overflow-hidden">
      {anyEnabled && (
        <div className="flex items-center justify-end gap-3 px-4 py-2 shrink-0">
          <AlertVisibilitySelector
            scopeKey={scopeKey}
            value={visibility}
            onChange={setVisibility}
          />
        </div>
      )}
      <DataTable<GroupedAlert>
        id={ALERTS_TABLE_CONFIG.tableId}
        mode="server"
        className="flex flex-col flex-1 min-h-0"
        containerClassName="flex-1"
        columns={columns}
        errorBoundaryLabel="Something went wrong in the Alerts table"
        search={searchConfig}
        features={{ enableRowExpansion: true, enableSorting: true }}
        rowExpansion={{
          icon: <ChevronRight className="h-4 w-4" />,
          getRowId: row => row.alertKey,
          render: row => {
            const occurrencesPath = `${alertsBasePath}/alerts/${encodeURIComponent(row.alertKey)}/occurrences`;
            const occurrencesUrl = searchParams.toString()
              ? `${occurrencesPath}?${searchParams.toString()}`
              : occurrencesPath;

            return (
              <div className="flex flex-col gap-2 min-w-0 max-w-full">
                <AlertDetailCard
                  alert={row}
                  organizationId={organizationId}
                  scope={{ kind: 'organization', organizationId }}
                  alertKey={row.alertKey}
                />
                <div className="flex justify-start">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={event => {
                      event.stopPropagation();
                      navigate(occurrencesUrl);
                    }}
                  >
                    <List className="h-4 w-4 mr-2" />
                    Show Occurrences
                  </Button>
                </div>
              </div>
            );
          },
          contentClassName: 'bg-muted/30 p-4',
          rowClassName: 'bg-muted/10',
          keepMounted: false,
          showContainer: false,
        }}
        queryFn={queryFn}
        getQueryKeysFromParams={queryKeyFn}
        countScope={countScope}
        rowSelection={rowSelectionOptions}
        getRowClassName={anyEnabled ? getRowClassName : undefined}
        noDataLabel="No alerts found."
        queryConfig={{
          staleTime: 30000,
          refetchOnWindowFocus: false,
        }}
      />
    </div>
  );
}

DataTableAlertsContent.displayName = 'DataTableAlertsContent';

export default function DataTableAlertsTab(): ReactElement | null {
  const { organizationId } = useParams<{ organizationId: string }>();

  if (!organizationId) {
    return null;
  }

  return (
    <DataTableAlertsContent
      key={organizationId}
      organizationId={organizationId}
    />
  );
}
